import template from "./sds_lookup.html";
import promisePoller, { CANCEL_TOKEN } from "promise-poller";
import { captureException, captureMessage } from "@sentry/browser";
import en from "./en.json";
import es from "./es.json";

const PENDING = "pending";
const RESOLVED = "resolved";
const FAILED = "failed";
const MAX_OCR_IMAGE_DIMENSION = 2048;
const WAITING_MESSAGES = [
    { key: "sdsLookup.WAIT_VERIFYING_PRODUCT_NAME", duration: 5000 },
    {
        key: "sdsLookup.WAIT_SEARCHING_INVENTORY",
        duration: 15000,
    },
    { key: "sdsLookup.WAIT_LOCATING_SDS", duration: 15000 },
    { key: "sdsLookup.WAIT_STILL_LOCATING_SDS", duration: 15000 },
    { key: "sdsLookup.WAIT_ALMOST_THERE_LOCATING_SDS", duration: 30000 },
    { key: "sdsLookup.WAIT_PREPARING_SDS" },
];

class SDSLookupController {
    /* @ngInject */
    constructor(
        $document,
        ImageShrinkService,
        $http,
        SDSLookupRequestService,
        $state,
        $window,
        $q,
        $timeout,
    ) {
        this.$document = $document;
        this.ImageShrinkService = ImageShrinkService;
        this.$http = $http;
        this.SDSLookupRequestService = SDSLookupRequestService;
        this.$state = $state;
        this.$window = $window;
        this.$q = $q;
        this.$timeout = $timeout;

        this.PENDING = PENDING;
        this.RESOLVED = RESOLVED;
        this.FAILED = FAILED;
    }

    $onInit() {
        let ctrl = this;
        ctrl.IDOptions = {};
        ctrl.submitting = false;
        ctrl.showSpinner = false;
        ctrl.showWarning = false;
        ctrl.warningMessageKey = "";
        ctrl.failedByStatus = false;
        ctrl.currentWaitingMessageKey = "";
        ctrl.waitingMessageIndex = 0;
        ctrl.waitingMessageTimeout = undefined;
    }

    $onDestroy() {
        let ctrl = this;
        ctrl.stopWaitingMessages();
    }

    startWaitingMessages() {
        let ctrl = this;
        ctrl.stopWaitingMessages();
        ctrl.waitingMessageIndex = 0;
        ctrl.currentWaitingMessageKey = WAITING_MESSAGES[0].key;
        ctrl.scheduleNextWaitingMessage();
    }

    scheduleNextWaitingMessage() {
        let ctrl = this;
        let currentStep = WAITING_MESSAGES[ctrl.waitingMessageIndex];
        if (!currentStep) {
            return;
        }
        if (!currentStep.duration) {
            return;
        }
        ctrl.waitingMessageTimeout = ctrl.$timeout(function () {
            ctrl.waitingMessageIndex += 1;
            let nextStep = WAITING_MESSAGES[ctrl.waitingMessageIndex];
            if (nextStep) {
                ctrl.currentWaitingMessageKey = nextStep.key;
                ctrl.scheduleNextWaitingMessage();
            }
        }, currentStep.duration);
    }

    stopWaitingMessages() {
        let ctrl = this;
        if (ctrl.waitingMessageTimeout) {
            ctrl.$timeout.cancel(ctrl.waitingMessageTimeout);
            ctrl.waitingMessageTimeout = undefined;
        }
        ctrl.currentWaitingMessageKey = "";
        ctrl.waitingMessageIndex = 0;
    }

    openFilePicker() {
        let ctrl = this;
        captureMessage("sdsLookup.image.filePicker.opened", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.openFilePicker",
                },
            },
        });
        ctrl.$document[0].getElementById("sdsLookupImage").click();
    }

    handleCameraPhoto(options) {
        let ctrl = this;
        if (!options || !options.file) {
            captureMessage("sdsLookup.image.photo.missingFile", {
                level: "warning",
                contexts: {
                    sds_lookup: {
                        branch: "sdsLookup.handleCameraPhoto.missingFile",
                    },
                },
            });
            return;
        }
        ctrl.showWarning = false;
        ctrl.warningMessageKey = "";
        ctrl.submitting = true;
        ctrl.showSpinner = true;
        ctrl.startWaitingMessages();

        captureMessage("sdsLookup.image.photo.received", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.handleCameraPhoto.receivedFile",
                    file_type: options.file && options.file.type,
                    file_size: options.file && options.file.size,
                },
            },
        });

        ctrl.ImageShrinkService.shrink(options.file, 1024 * 1024, {
            maxDimension: MAX_OCR_IMAGE_DIMENSION,
        }).then(
            function (blob) {
                captureMessage("sdsLookup.image.photo.shrink.success", {
                    contexts: {
                        sds_lookup: {
                            branch: "sdsLookup.handleCameraPhoto.shrinkSuccess",
                            blob_type: blob && blob.type,
                            blob_size: blob && blob.size,
                        },
                    },
                });
                ctrl.uploadImageAndCreateRequest(blob);
            },
            function (error) {
                captureMessage("sdsLookup.image.photo.shrink.failed", {
                    level: "error",
                    contexts: {
                        sds_lookup: {
                            branch: "sdsLookup.handleCameraPhoto.shrinkFailed",
                            file_type: options.file && options.file.type,
                            file_size: options.file && options.file.size,
                            error_message: error && error.message,
                        },
                    },
                });
                captureException(
                    error ||
                        new Error(
                            "Error building upload blob image for SDS lookup",
                        ),
                    {
                        contexts: {
                            error: {
                                branch: "sdsLookup.handleCameraPhoto.buildUploadBlob",
                                file_type: options.file && options.file.type,
                                file_size: options.file && options.file.size,
                            },
                        },
                    },
                );
                ctrl.showLookupFailed("sdsLookup.IMAGE_PROCESSING_FAILED");
            },
        );
    }

    uploadImageAndCreateRequest(file) {
        let ctrl = this;
        let fd = new FormData();
        fd.append("file", file);
        fd.append("upload_preset", "sds_lookup");
        fd.append("folder", "sds_lookup_images");

        captureMessage("sdsLookup.image.upload.started", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.uploadImageAndCreateRequest.started",
                    file_type: file && file.type,
                    file_size: file && file.size,
                },
            },
        });

        ctrl.$http
            .post("https://api.cloudinary.com/v1_1/harvust/image/upload", fd, {
                headers: { "Content-Type": undefined },
                skipAuthorization: true,
            })
            .then(
                function (response) {
                    let publicId = response.data && response.data.public_id;
                    captureMessage("sdsLookup.image.upload.completed", {
                        contexts: {
                            sds_lookup: {
                                branch: "sdsLookup.uploadImageAndCreateRequest.completed",
                                has_public_id: !!publicId,
                            },
                        },
                    });
                    if (!publicId) {
                        captureMessage(
                            "sdsLookup.image.upload.missingPublicId",
                            {
                                level: "error",
                                contexts: {
                                    sds_lookup: {
                                        branch: "sdsLookup.uploadImageAndCreateRequest.missingPublicId",
                                    },
                                },
                            },
                        );
                        ctrl.showLookupFailed("sdsLookup.IMAGE_UPLOAD_FAILED");
                        return;
                    }
                    ctrl.createLookupRequest({
                        input_image_id: publicId,
                    });
                },
                function (error) {
                    captureMessage("sdsLookup.image.upload.failed", {
                        level: "error",
                        contexts: {
                            sds_lookup: {
                                branch: "sdsLookup.uploadImageAndCreateRequest.failed",
                                status_code: error && error.status,
                                error_message: error && error.statusText,
                            },
                        },
                    });
                    ctrl.showLookupFailed("sdsLookup.IMAGE_UPLOAD_FAILED");
                },
            );
    }

    submitText() {
        let ctrl = this;
        if (!ctrl.lookupText) {
            return;
        }
        ctrl.showWarning = false;
        ctrl.warningMessageKey = "";
        ctrl.submitting = true;
        ctrl.showSpinner = true;
        ctrl.startWaitingMessages();
        ctrl.createLookupRequest({
            input_name: ctrl.lookupText,
        });
    }

    createLookupRequest(payload) {
        let ctrl = this;
        let isImageLookup = !!(payload && payload.input_image_id);
        let requestPayload = {
            ...payload,
            grower_organization: ctrl.growerSid,
        };

        captureMessage("sdsLookup.request.create.started", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.createLookupRequest.started",
                    is_image_lookup: isImageLookup,
                    has_input_name: !!(payload && payload.input_name),
                },
            },
        });

        ctrl.SDSLookupRequestService.create(requestPayload).then(
            function (lookupRequest) {
                captureMessage("sdsLookup.request.create.succeeded", {
                    contexts: {
                        sds_lookup: {
                            branch: "sdsLookup.createLookupRequest.succeeded",
                            is_image_lookup: isImageLookup,
                            request_sid: lookupRequest && lookupRequest.sid,
                        },
                    },
                });
                ctrl.pollRequest(lookupRequest.sid);
            },
            function (error) {
                captureMessage("sdsLookup.request.create.failed", {
                    level: "error",
                    contexts: {
                        sds_lookup: {
                            branch: "sdsLookup.createLookupRequest.failed",
                            is_image_lookup: isImageLookup,
                            status_code: error && error.status,
                            error_message: error && error.statusText,
                        },
                    },
                });
                ctrl.showLookupFailed(
                    error && error.status === 400
                        ? "sdsLookup.HAZCOM_NOT_ENABLED"
                        : "sdsLookup.CREATE_LOOKUP_FAILED",
                );
            },
        );
    }

    pollRequest(requestSid) {
        let ctrl = this;
        ctrl.showSpinner = true;
        ctrl.failedByStatus = false;

        captureMessage("sdsLookup.request.poll.started", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.pollRequest.started",
                    request_sid: requestSid,
                },
            },
        });

        ctrl.$q
            .when(
                promisePoller({
                    retries: 300,
                    interval: 1000,
                    taskFn: function () {
                        return ctrl.SDSLookupRequestService.retrieve(
                            requestSid,
                        ).then(function (lookupRequest) {
                            if (lookupRequest.status === ctrl.RESOLVED) {
                                captureMessage(
                                    "sdsLookup.request.poll.resolved",
                                    {
                                        contexts: {
                                            sds_lookup: {
                                                branch: "sdsLookup.pollRequest.resolvedStatus",
                                                request_sid: requestSid,
                                            },
                                        },
                                    },
                                );
                                return lookupRequest;
                            }
                            if (lookupRequest.status === ctrl.FAILED) {
                                captureMessage(
                                    "sdsLookup.request.poll.failedStatus",
                                    {
                                        level: "warning",
                                        contexts: {
                                            sds_lookup: {
                                                branch: "sdsLookup.pollRequest.failedStatus",
                                                request_sid: requestSid,
                                            },
                                        },
                                    },
                                );
                                ctrl.failedByStatus = true;
                                return ctrl.$q.reject(CANCEL_TOKEN);
                            }
                            return ctrl.$q.reject(lookupRequest);
                        });
                    },
                }),
            )
            .then(
                function (lookupRequest) {
                    let sdsUrl =
                        lookupRequest.sds_product &&
                        lookupRequest.sds_product.sds_url;
                    if (sdsUrl) {
                        captureMessage(
                            "sdsLookup.request.poll.redirectingToSds",
                            {
                                contexts: {
                                    sds_lookup: {
                                        branch: "sdsLookup.pollRequest.redirectingToSds",
                                        request_sid: requestSid,
                                    },
                                },
                            },
                        );
                        ctrl.$window.location.href = sdsUrl;
                    } else {
                        captureMessage("sdsLookup.request.poll.missingSdsUrl", {
                            level: "error",
                            contexts: {
                                sds_lookup: {
                                    branch: "sdsLookup.pollRequest.missingSdsUrl",
                                    request_sid: requestSid,
                                },
                            },
                        });
                        ctrl.showLookupFailed("sdsLookup.MISSING_SDS_URL");
                    }
                },
                function (error) {
                    if (ctrl.failedByStatus) {
                        captureMessage(
                            "sdsLookup.request.poll.rejectedFailedStatus",
                            {
                                level: "warning",
                                contexts: {
                                    sds_lookup: {
                                        branch: "sdsLookup.pollRequest.rejectedFailedStatus",
                                        request_sid: requestSid,
                                        rejection_value: String(error),
                                    },
                                },
                            },
                        );
                        ctrl.showLookupFailed("sdsLookup.LOOKUP_FAILED");
                    } else {
                        captureMessage(
                            "sdsLookup.request.poll.rejectedTimeout",
                            {
                                level: "error",
                                contexts: {
                                    sds_lookup: {
                                        branch: "sdsLookup.pollRequest.rejectedTimeout",
                                        request_sid: requestSid,
                                        rejection_value: String(error),
                                    },
                                },
                            },
                        );
                        ctrl.showLookupFailed("sdsLookup.LOOKUP_TIMEOUT");
                    }
                },
            )
            .finally(function () {
                captureMessage("sdsLookup.request.poll.finished", {
                    contexts: {
                        sds_lookup: {
                            branch: "sdsLookup.pollRequest.finished",
                            request_sid: requestSid,
                            failed_by_status: ctrl.failedByStatus,
                        },
                    },
                });
                ctrl.submitting = false;
                ctrl.showSpinner = false;
                ctrl.stopWaitingMessages();
            });
    }

    showLookupFailed(messageKey) {
        let ctrl = this;
        captureMessage("sdsLookup.lookup.failed", {
            level: "warning",
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.showLookupFailed",
                    warning_message_key: messageKey,
                },
            },
        });
        ctrl.warningMessageKey = messageKey;
        ctrl.showWarning = true;
        ctrl.submitting = false;
        ctrl.showSpinner = false;
        ctrl.stopWaitingMessages();
    }

    reloadPage() {
        let ctrl = this;
        captureMessage("sdsLookup.reloadPage", {
            contexts: {
                sds_lookup: {
                    branch: "sdsLookup.reloadPage",
                },
            },
        });
        ctrl.$state.reload();
    }
}

export default {
    name: "sdsLookup",
    template: template,
    controller: SDSLookupController,
    bindings: {
        growerSid: "<",
    },
    translations: { en, es },
};
